import { PrizeDisplayGroup } from '@daymade/common-lib/dbEnums';
import CardWithBackgroundImage from 'components/CardWithBackgroundImage';
import FavouriteButton from 'components/prizes/FavouriteButton';
import PrizeTierBadge from 'components/prizes/PrizeTierBadge';
import UnfavouritePrizeModal from 'components/wish-list/UnfavouritePrizeModal';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { ReactNode, useState } from 'react';
import { prizeApiClient } from 'utils/apiClient';
import { capitalizeFirstLetter, getSubscriberStatus } from 'utils/common';
import { ASSET_PREFIX, colors, SubscriberStatus } from 'utils/constants';

type TPrize = {
  id: number;
  title: string;
  subheading: string;
  heroImageLocation: string;
  tierCode?: string;
  drawTypePlans?: string[];
  isFromPlayersSuggestion?: boolean | null;
  position?: number; // in prize display group
};

const updateFavourite = async (
  prizeId: number,
  newValue: boolean,
  refreshStore: () => Promise<void>,
) => {
  if (newValue) {
    await prizeApiClient.auth.favouritePrize({
      body: { prizeId },
    });
  } else {
    await prizeApiClient.auth.unfavouritePrize({
      body: { prizeId },
    });
  }
  await refreshStore();
};

interface IPrizeCardProps {
  prize: TPrize;
  showPrizeTierBadge?: boolean;
  additionalComponentBadge: ReactNode | null;
  onClick?: (event: React.MouseEvent<any>) => void;
  href?: string;
  withFavouriteButton?: boolean;
  setShowUnfavouritePrizeModal: (show: boolean) => void;
  showLockedPrizeBannerIfApplicable?: boolean;
  displayGroup?: PrizeDisplayGroup; // for admin site only
  customImageOpacity?: number;
  additionalCardClass?: string;
  rebrandDm2025?: boolean;
}

const PrizeCard = observer(
  ({
    prize,
    showPrizeTierBadge,
    additionalComponentBadge,
    onClick,
    href,
    withFavouriteButton,
    setShowUnfavouritePrizeModal,
    showLockedPrizeBannerIfApplicable,
    displayGroup,
    customImageOpacity,
    additionalCardClass = undefined,
    rebrandDm2025,
  }: IPrizeCardProps) => {
    const {
      isFromPlayersSuggestion: playersChoice,
      id: prizeId,
      heroImageLocation,
      tierCode,
      drawTypePlans: mainDrawDrawTypePlans,
    } = prize;

    const {
      currentUser,
      favouritedPrizeIds,
      fetchWishListPrizes,
      subscriptionStatusInfo,
    } = useUserStore();
    const router = useRouter();

    const subscriberStatus = getSubscriberStatus(subscriptionStatusInfo);

    // Show locked prize banner if the prize is not winnable for the drawTypePlan user is subscribed to
    const showLockedPrizeBanner =
      showLockedPrizeBannerIfApplicable &&
      subscriberStatus !== SubscriberStatus.NON_SUBSCRIBER &&
      mainDrawDrawTypePlans &&
      mainDrawDrawTypePlans.length > 0 &&
      !mainDrawDrawTypePlans.includes(subscriberStatus);

    const handleFavouriteToggle = async (newValue: boolean) => {
      // If user is not logged in and they try to favourite a prize, redirect to /account/wish-list
      if (!currentUser) {
        await router.push('/account/wish-list');
        return;
      }

      if (!newValue) {
        // Show the confirmation modal.
        setShowUnfavouritePrizeModal(true);
      } else {
        // Toggling on, so no modal, just do it.
        await updateFavourite(prizeId, newValue, fetchWishListPrizes);
      }
    };

    return (
      <CardWithBackgroundImage
        className={additionalCardClass}
        id="prizeCard"
        src={heroImageLocation} // TODO: Use WebP image wherever possible
        fallbackImagePath={heroImageLocation} // TODO: Should be fallbackImage (a JPEG fallback)
        imageOpacity={customImageOpacity || 0.8}
        onClick={onClick}
      >
        <div
          className={
            'invisibleDivForSizing' + (rebrandDm2025 ? ' rebrandDm2025' : '')
          }
        />

        <a href={href ? href : undefined} rel="noopener noreferrer">
          <div className="cardContents fullWidth flexCentered">
            {tierCode && showPrizeTierBadge && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  position: 'absolute',
                  top: '5px',
                  left: '5px',
                  margin: 0,
                  right: '30px',
                }}
              >
                <PrizeTierBadge prizeTier={tierCode} />
              </div>
            )}

            {displayGroup ? (
              <div
                className="removePrizeBadge"
                onClick={async () => {
                  await prizeApiClient.admin.removePrizeFromDisplayGroup({
                    params: { group: displayGroup, prizeId },
                  });
                }}
              >
                <div className="crossIcon flexCentered">
                  <span style={{ fontWeight: 600, marginTop: '-1px' }}>x</span>
                </div>
              </div>
            ) : rebrandDm2025 && withFavouriteButton ? undefined : (
              <h2
                style={{
                  fontWeight: 'normal',
                  fontSize: '30px',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  margin: 0,
                  color: 'white',
                }}
              >
                +
              </h2>
            )}

            {additionalComponentBadge}

            {playersChoice && (
              <img
                src={`${ASSET_PREFIX}/images/prizes/players-choice.png`}
                style={{ height: 'auto', width: '80px', alignSelf: 'center' }}
                alt="players' choice"
              />
            )}
          </div>
        </a>

        <div
          style={{
            position: 'absolute',
            [rebrandDm2025 ? 'top' : 'bottom']: 0,
            right: 0,
            left: 0,
          }}
        >
          {withFavouriteButton && (
            <FavouriteButton
              favourited={favouritedPrizeIds.has(prize.id)}
              onToggle={handleFavouriteToggle}
              rebrand2025
              style={{
                position: 'absolute',
                left: rebrandDm2025 ? undefined : 5,
                bottom: rebrandDm2025
                  ? undefined
                  : showLockedPrizeBanner
                    ? 50
                    : 5,
                right: rebrandDm2025 ? 15 : undefined,
                top: rebrandDm2025 ? 16 : undefined,
              }}
            />
          )}

          {showLockedPrizeBanner && (
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                padding: '0.5em',
                color: colors.primaryBrandGray,
                whiteSpace: 'break-spaces',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '3px 3px 10px 10px',
              }}
            >
              <img
                src={`${ASSET_PREFIX}/images/prizes/padlock.svg`}
                alt=""
                style={{
                  height: '15px',
                  marginRight: '1em',
                }}
              />
              <p
                style={{
                  fontWeight: 900,
                  marginBottom: 0,
                  fontSize: '0.9em',
                }}
              >
                {mainDrawDrawTypePlans.map(capitalizeFirstLetter).join('/')}{' '}
                Prize
              </p>
            </div>
          )}
        </div>

        <style jsx>
          {`
            .cardContents {
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
            }

            .invisibleDivForSizing {
              padding-top: 100%;
            }

            .rebrandDm2025.invisibleDivForSizing {
              padding-top: 69%;
            }
            @media (min-width: 768px) {
              .rebrandDm2025.invisibleDivForSizing {
                padding-top: 73%;
              }
            }

            .removePrizeBadge {
              position: absolute;
              top: 0.6em;
              right: 0.6em;
              margin: 0;
              border: 2px solid white;
              border-radius: 100%;
              padding: 1em;
            }

            .removePrizeBadge:hover {
              background: #ffffff40;
            }

            .removePrizeBadge .crossIcon {
              position: absolute;
              top: 50%;
              left: 0;
              font-weight: 800;
              font-size: 1.25em;
              width: 100%;
              color: white;
              line-height: 0;
            }

            .arrow {
              margin: 0.2em;
              width: 15%;
              cursor: pointer;
            }
          `}
        </style>
      </CardWithBackgroundImage>
    );
  },
);

interface IPrizeCardWithTitleBelowProps {
  prize: TPrize;
  showPrizeTierBadge?: boolean;
  onClick?: (event: React.MouseEvent<any>) => void;
  withFavouriteButton?: boolean;
  href?: string;
  customImageOpacity?: number;
  additionalCardClass?: string;
  showLockedPrizeBannerIfApplicable?: boolean;
  displayGroup?: PrizeDisplayGroup; // for admin site only
  style?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  subheadingStyle?: React.CSSProperties;
  redeemPrizeButton?: ReactNode;
  rebrandDm2025?: boolean;
}

const PrizeCardWithTitleBelow = ({
  prize,
  withFavouriteButton,
  onClick,
  href,
  style,
  titleStyle,
  subheadingStyle,
  showLockedPrizeBannerIfApplicable = false,
  showPrizeTierBadge = true,
  displayGroup,
  customImageOpacity,
  redeemPrizeButton = undefined,
  additionalCardClass = '',
  rebrandDm2025,
}: IPrizeCardWithTitleBelowProps) => {
  const { fetchWishListPrizes } = useUserStore();

  const [showUnfavouritePrizeModal, setShowUnfavouritePrizeModal] =
    useState(false);

  if (!prize) return null;

  const {
    title,
    subheading /* ENG-394 "code" was used before, but now has a differnet meaning. See below */,
    id: prizeId,
    heroImageLocation,
  } = prize;

  const additionalComponentBadge =
    /* ENG-394 - Previously, prizes could be "tagged" with a code, and it made the badge appear. The use of
    "code" was a bit strange. There should be something like "badge" or "prize_partner". See new ticket: ENG-414
    
    code === 'LASTMINUTE' ? (
      <div
        style={{
          color: 'white',
          fontStyle: 'italic',
          fontWeight: 'bold',
          fontSize: '0.8em',
        }}
      >
        in partnership with
        <br />
        <img
          src={`${ASSET_PREFIX}/images/prizes/lastminute.jpg`}
          style={{ width: '90%', maxWidth: '100px', marginTop: '0.5em' }}
          alt="lastminute.com logo"
        />
      </div>
    ) :
     */ null;

  return (
    <div
      className={
        'prizeCardWithTitleBelow' + (rebrandDm2025 ? ' rebrandDm2025' : '')
      }
      style={style}
    >
      {showUnfavouritePrizeModal && (
        <UnfavouritePrizeModal
          onConfirm={async () => {
            await updateFavourite(prizeId, false, fetchWishListPrizes);
            setShowUnfavouritePrizeModal(false);
          }}
          onClose={() => setShowUnfavouritePrizeModal(false)}
          prizeName={title}
          prizeHero={heroImageLocation}
        />
      )}

      <PrizeCard
        additionalCardClass={additionalCardClass}
        prize={prize}
        showPrizeTierBadge={showPrizeTierBadge}
        additionalComponentBadge={additionalComponentBadge}
        onClick={onClick}
        href={href}
        setShowUnfavouritePrizeModal={setShowUnfavouritePrizeModal}
        withFavouriteButton={withFavouriteButton}
        showLockedPrizeBannerIfApplicable={showLockedPrizeBannerIfApplicable}
        displayGroup={displayGroup}
        customImageOpacity={customImageOpacity}
        rebrandDm2025={rebrandDm2025}
      />

      <a
        className={
          'no-underline cursor-pointer textContainer' +
          (rebrandDm2025 ? ' withMinimalSpacing' : '')
        }
        onClick={onClick}
        href={href}
      >
        <p
          className={
            rebrandDm2025
              ? 'aboutUsTypographyHeaderMedium rebrandDm2025PrizeTitle'
              : 'prizeTitle'
          }
          style={{ ...titleStyle }}
        >
          {title}
        </p>

        <p className="prizeSubheading" style={{ ...subheadingStyle }}>
          {subheading}
        </p>
      </a>
      {redeemPrizeButton && redeemPrizeButton}

      <style jsx>{`
        .prizeCardWithTitleBelow {
          min-width: 140px;
          width: 45%;
          max-width: 320px;
          margin: 1em 0.5em 1em 0.5em;
        }

        :global(.prizeCardWithTitleBelow .cardWithBackgroundImage) {
          position: relative;
          background-color: black;
          border-radius: 15px;
          box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.15);
          padding: 2%;
          cursor: ${onClick ? 'pointer' : 'default'};
        }

        :global(.prizeCardWithTitleBelow.rebrandDm2025) {
          border-radius: 18px;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          :global(.prizeCardWithTitleBelow.rebrandDm2025) {
            border-radius: 24px;
          }
        }

        :global(.prizeCardWithTitleBelow.rebrandDm2025:after) {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 33%;
          background: linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.5) 0,
            rgba(0, 0, 0, 0) 100%
          );
        }

        .prizeTitle,
        .prizeSubheading {
          margin: 0.5em;
          line-height: 1.35;
        }

        .prizeTitle {
          font-size: 1.1em;
          font-weight: 800;
        }

        .prizeSubheading {
          font-weight: 700;
        }

        a {
          white-space: normal;
          text-align: left;
        }

        @media (min-width: 700px) {
          .prizeCardWithTitleBelow {
            width: 30%;
          }

          .prizeTitle {
            font-size: 1.3em;
          }
        }
        .prizeCardWithTitleBelow.rebrandDm2025 {
          width: 100%;
          min-width: 0;
          max-width: 9999px;
          margin: 0;
          position: relative;
        }

        .rebrandDm2025 .textContainer {
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          display: block;
          padding: 16px;
          color: #fff;
          z-index: 1;
        }
        @media (min-width: 768px) {
        }

        .rebrandDm2025 .prizeSubheading {
          display: none;
        }

        .rebrandDm2025PrizeTitle {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      `}</style>
    </div>
  );
};

export default PrizeCardWithTitleBelow;
